import React, { useMemo, useEffect } from 'react';
import {
  SandpackProvider,
  SandpackLayout,
  SandpackCodeEditor,
  SandpackFileExplorer,
  SandpackPreview,
  useSandpack,
} from '@codesandbox/sandpack-react';

// Tailwind CDN injected into the Sandpack environment so the generated UI looks polished
const TAILWIND_INDEX_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Transformerus Build Preview</title>
  <script src="https://cdn.tailwindcss.com"></script>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
  <style>
    body { font-family: 'Inter', system-ui, -apple-system, sans-serif; margin: 0; }
  </style>
</head>
<body>
  <div id="root"></div>
</body>
</html>`;

const FALLBACK_APP = `export default function App() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 text-neutral-500">
      <div className="text-center p-8">
        <div className="text-2xl font-bold text-neutral-800 mb-2">Live preview ready</div>
        <p>Describe what you want to build to see it render here.</p>
      </div>
    </div>
  );
}`;

const PROJECT_PKG_JSON = JSON.stringify(
  {
    name: 'transformerus-preview',
    main: '/App.js',
    dependencies: {
      'react': '^18.3.1',
      'react-dom': '^18.3.1',
      'react-scripts': '5.0.1',
      'lucide-react': '^0.439.0',
      'react-router-dom': '^6.26.0',
      'recharts': '^2.12.7',
      'framer-motion': '^11.3.0',
      'date-fns': '^3.6.0',
      'clsx': '^2.1.1',
    },
  },
  null,
  2,
);

// Inner listener — subscribes to Sandpack bundler messages and surfaces any
// "Could not find dependency" or "Module not found" errors to the parent
// Builder via the `onDependencyError(packageName)` callback. Mounted INSIDE
// `<SandpackProvider>` so it has access to the `useSandpack` hook.
function DependencyErrorListener({ onDependencyError }) {
  const { listen } = useSandpack();

  useEffect(() => {
    if (typeof onDependencyError !== 'function' || typeof listen !== 'function') return;

    const stop = listen((msg) => {
      // Sandpack emits 'action' messages for errors. We're hunting for the
      // bundler's "dependency not found" variants.
      const text =
        (msg && (msg.message || msg.error || msg.title)) ||
        (msg && msg.payload && (msg.payload.error || msg.payload.message)) ||
        '';
      if (typeof text !== 'string' || !text) return;

      // Common patterns Sandpack/Codesandbox bundler emits:
      //   "Could not find dependency: 'X' relative to '...'"
      //   "Module not found: Can't resolve 'X'"
      //   "Failed to resolve module specifier 'X'"
      const patterns = [
        /Could not find dependency:\s*['"]([^'"]+)['"]/i,
        /Cannot find module\s+['"]([^'"]+)['"]/i,
        /Can't resolve\s+['"]([^'"]+)['"]/i,
        /Failed to resolve module specifier\s+['"]([^'"]+)['"]/i,
      ];
      for (const re of patterns) {
        const m = text.match(re);
        if (m && m[1]) {
          onDependencyError(m[1]);
          return;
        }
      }
    });

    return () => {
      if (typeof stop === 'function') stop();
    };
  }, [listen, onDependencyError]);

  return null;
}


export default function SandpackBuilder({ files, entry = '/App.js', activeView = 'preview', onDependencyError }) {
  // Always inject our Tailwind-loaded index.html into the project
  const sandpackFiles = useMemo(() => {
    const out = {};
    const map = files && Object.keys(files).length > 0 ? files : { '/App.js': FALLBACK_APP };
    Object.entries(map).forEach(([path, code]) => {
      out[path] = { code: typeof code === 'string' ? code : String(code ?? '') };
    });
    out['/public/index.html'] = { code: TAILWIND_INDEX_HTML, hidden: true };
    // Explicit package.json — Sandpack's bundler reads dependency list from
    // this file when resolving imports. Without it, even customSetup deps may
    // be missed for some import paths.
    out['/package.json'] = { code: PROJECT_PKG_JSON, hidden: true };
    return out;
  }, [files]);

  // Sandpack key forces a remount whenever the file set materially changes,
  // ensuring the preview re-bundles cleanly after each generation.
  const remountKey = useMemo(() => {
    return Object.keys(sandpackFiles).sort().join('|') + ':' + (sandpackFiles[entry]?.code?.length || 0);
  }, [sandpackFiles, entry]);

  return (
    <div className="h-full w-full" data-testid="sandpack-builder">
      <SandpackProvider
        key={remountKey}
        template="react"
        theme="light"
        files={sandpackFiles}
        options={{
          activeFile: entry,
          visibleFiles: Object.keys(sandpackFiles).filter((p) => !p.startsWith('/public/')),
          recompileMode: 'delayed',
          recompileDelay: 600,
        }}
        customSetup={{
          dependencies: {
            'lucide-react': '^0.439.0',
            'react-router-dom': '^6.26.0',
            'recharts': '^2.12.7',
            'framer-motion': '^11.3.0',
            'date-fns': '^3.6.0',
            'clsx': '^2.1.1',
          },
        }}
      >
        <DependencyErrorListener onDependencyError={onDependencyError} />
        <SandpackLayout style={{ height: '100%', border: 'none', borderRadius: 0 }}>
          {activeView === 'code' ? (
            <>
              <SandpackFileExplorer style={{ height: '100%', minWidth: 180, maxWidth: 240 }} />
              <SandpackCodeEditor
                style={{ height: '100%', flex: 1 }}
                showLineNumbers
                showTabs
                wrapContent
              />
            </>
          ) : (
            <SandpackPreview style={{ height: '100%', flex: 1 }} showOpenInCodeSandbox={false} showRefreshButton />
          )}
        </SandpackLayout>
      </SandpackProvider>
    </div>
  );
}
